import { SearchFormProps } from '@/reports/index';
import { namesMatch } from '@/utils/index';
import { Make } from './YMMSearchForm/ymmSearch';

const STANDARD_VIN_LENGTH = 17;
const MIN_NON_STANDARD_VIN_LENGTH = 5;
const MAX_NON_STANDARD_VIN_LENGTH = 16;
const ALPHANUMERIC_REGEX = new RegExp('^[a-zA-Z0-9]+$');
const VIN_VALIDATION_REGEX = new RegExp(
  '^[A-HJ-NPR-Z0-9]{8}[0-9xX][A-HJ-NPR-Z0-9]{8}$'
);
const LICENSE_PLATE_VALIDATION_REGEX = new RegExp(
  '^(?=.*[a-zA-Z0-9])[a-zA-Z0-9-]{1,10}$'
);

function checkDigitCheck(vin: string): boolean {
  const transliteration: { [key: string]: number } = {
    A: 1,
    B: 2,
    C: 3,
    D: 4,
    E: 5,
    F: 6,
    G: 7,
    H: 8,
    J: 1,
    K: 2,
    L: 3,
    M: 4,
    N: 5,
    P: 7,
    R: 9,
    S: 2,
    T: 3,
    U: 4,
    V: 5,
    W: 6,
    X: 7,
    Y: 8,
    Z: 9,
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    0: 0,
  };

  const weights = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

  const chars = vin.split('');
  const product = chars.reduce((total, char, index) => {
    return total + transliteration[char] * weights[index];
  }, 0);

  let expectedCheckDigit: number | 'X' = product % 11;
  if (expectedCheckDigit === 10) {
    expectedCheckDigit = 'X';
  }

  return expectedCheckDigit.toString() === chars[8];
}

export function isValidVIN(input: string | undefined): boolean {
  if (input) {
    input = input.toUpperCase().trim();

    // Standard VINs
    if (input.length === STANDARD_VIN_LENGTH) {
      return VIN_VALIDATION_REGEX.test(input) && checkDigitCheck(input);
    }

    // Non-standard VINs
    if (
      input.length >= MIN_NON_STANDARD_VIN_LENGTH &&
      input.length <= MAX_NON_STANDARD_VIN_LENGTH
    ) {
      return ALPHANUMERIC_REGEX.test(input);
    }

    return false;
  }

  return false;
}

export function isValidLicensePlate(input: string | undefined): boolean {
  if (input) {
    input = input.toUpperCase().trim();
    return LICENSE_PLATE_VALIDATION_REGEX.test(input);
  }

  return false;
}

const isValidInput = (
  options: string[],
  value: string
): boolean | undefined => {
  if (options) {
    const result = options.some((option) => namesMatch(option, value));
    return result;
  }
};

export function getYears() {
  const years = [];
  const startYear = 1980;
  let nextYear = new Date().getFullYear() + 1;
  while (nextYear >= startYear) {
    years.push((nextYear--).toString());
  }
  return years;
}

export function getMakes(makesObj: Make[]): string[] {
  return makesObj.map((make) => {
    return make.name;
  });
}

export function getModels(makeName: string, makes: Make[]): string[] {
  if (makes && makeName) {
    const make = makes.find((make) => namesMatch(make.name, makeName));
    if (make && make.models) {
      return make.models.map((model) => {
        return model.name;
      });
    }
  }
  return [];
}

export function isValidYear(
  years: string[],
  value: string
): boolean | undefined {
  return isValidInput(years, value);
}

export function isValidMake(
  makes: string[],
  value: string
): boolean | undefined {
  return isValidInput(makes, value);
}

export function isValidModel(
  models: string[],
  value: string
): boolean | undefined {
  return isValidInput(models, value);
}

export function isValidTrim(
  trims: string[],
  value: string
): boolean | undefined {
  return isValidInput(trims, value);
}

interface customSearchTab {
  label: string;
  content: JSX.Element;
  customHandler?: () => void;
  trackClick?: () => void;
}

interface OptionalProps {
  yearLabelHidden?: boolean;
  yearWidth?: number;
  makeLabelHidden?: boolean;
  makeWidth?: number;
  modelLabelHidden?: boolean;
  modelWidth?: number;
  trimHidden?: boolean;
  trimWidth?: number;
  trimLabelHidden?: boolean;
  buttonWidth?: number;
  searchStartIcon?: JSX.Element;
  formStyle?: React.CSSProperties;
  vinLabelHidden?: boolean;
  vinWidth?: string;
  selectHeight?: string;
  licensePlateLabelHidden?: boolean;
  licensePlateWidth?: string;
  licensePlateComponentsWidth?: number;
  stateLabelHidden?: boolean;
  stateWidth?: string;
  alignItems?: string;
}

export interface VehicleSearchProps extends SearchFormProps {
  vinInformationOptions?: boolean;
  clearFields?: boolean;
  searchButtonText?: string;
  searchButtonSize?: string;
  mobileView?: boolean;
  informationOptions?: boolean;
  additionalTabs?: customSearchTab[];
  optionalProps?: OptionalProps;
  isDashboardSearchHub?: boolean;
  ddPlan?: string;
}
